import * as React from 'react'

import { useQuery } from '@apollo/client/react/hooks'
import { Alert, Box, NoticeBox, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { DocumentsCustomFieldSettingsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import LabeledCheckbox from 'modules/core/components/Inputs/LabeledCheckbox'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'
import type { ChoiceOption } from 'modules/custom_resources/store/models/customFields/Field'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type Field from 'modules/custom_resources/store/models/customResources/Field'

import Choices from './Choices'
import CustomModalFooter from './CustomModalFooter'
import RangeInput from './RangeInput'
import canEditField from './canEditField'

const fieldTypeOptions = (allowedFieldTypes: string[]): Array<Record<string, string>> => {
  const options = [
    {
      label: i18n.t('settings.custom_fields.new.labels.text'),
      value: FieldTypes.text,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.long_text'),
      value: FieldTypes.longText,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.date'),
      value: FieldTypes.date,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.number'),
      value: FieldTypes.cents,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.rating'),
      value: FieldTypes.rating,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.checkbox'),
      value: FieldTypes.checkbox,
    },
    {
      label: i18n.t('settings.custom_fields.new.labels.single_choice'),
      value: FieldTypes.singleChoice,
    },
  ]

  return allowedFieldTypes && allowedFieldTypes.length > 0
    ? options.filter((option) => allowedFieldTypes.includes(option.value))
    : options
}

interface Props {
  readonly?: boolean
  field?: Field | CustomField | null
  duplicate?: boolean
  onClose?: () => void
  onSubmit?: (data: Record<string, unknown>) => Promise<unknown>
  allowPermissions?: boolean
  modalIllustration?: string
  modalTitle?: string
  modalDescription?: string
  modalPlaceholder?: string
  modalLabel?: string
  modalButton?: string
  modalAlert?: string
  showAllowedFieldTypes?: boolean
  showRequired?: boolean
  // When allowedFieldTypes is undefined, all types are allowed.
  // When allowedFieldTypes is the empty array, Text type is assumed.
  allowedFieldTypes?: string[]
  trackingProperties?: TrackingProperties
}

const range: [number, number] = [0, 10]

const defaultsFieldData = {
  required: true,
  options: [''],
  min_value: range[0],
  max_value: range[1],
  choice_options: [],
  visible: '',
  editable: '',
}

class FieldForm extends Form {
  constructor(values: {}) {
    super(values, {
      label: 'string',
      field_type: 'string',
      required: 'boolean',
      visible: 'string',
      editable: 'string',
      options: 'string',
      choice_options: 'string',
      min_value: 'number',
      max_value: 'number',
    })
  }
}

const FieldModal = ({
  readonly = false,
  modalIllustration,
  modalTitle,
  modalDescription,
  modalLabel,
  modalPlaceholder,
  modalButton,
  modalAlert,
  field,
  duplicate = false,
  allowedFieldTypes,
  allowPermissions,
  showAllowedFieldTypes = true,
  showRequired = true,
  onSubmit,
  onClose,
  trackingProperties,
}: Props) => {
  const existingField = !!field?.attributes.id

  const options = React.useMemo(
    () => fieldTypeOptions(allowedFieldTypes ?? []),
    [allowedFieldTypes]
  )

  const defaultData = {
    ...defaultsFieldData,
    field_type: options.length === 1 ? options[0]?.value : null,
  }

  const getFieldData = () => {
    if (duplicate) {
      return {
        ...defaultData,
        label: field?.attributes.label,
        field_type: field?.attributes.field_type,
        required: field?.attributes.required,
        choice_options: (field?.attributes.choice_options || defaultData.choice_options).map(
          (choice: ChoiceOption) => ({ ...choice, id: undefined })
        ),
        min_value: field?.attributes.min_value || defaultData.min_value,
        max_value: field?.attributes.max_value || defaultData.max_value,
      }
    }

    return field?.toJS() || defaultData
  }

  const form = React.useRef(new FieldForm(getFieldData())).current

  const [ratingRange, setRatingRange] = React.useState([
    form.get('min_value').value,
    form.get('max_value').value,
  ])

  if (options.length === 0 && !form.get('field_type').value) {
    // We default to Text field type if we don't allow to choose
    const field = form.get('field_type')
    field.set('Text')
  }

  if (!allowPermissions) {
    const visible = form.get('visible')
    const editable = form.get('editable')
    // To know if the form is ready to submit, it's used form.isComplete
    // Due to, we must set default data in this fields when permissions
    // aren't allowed
    visible.set('skip-permissions')
    editable.set('skip-permissions')
  }

  const getPayload = () => {
    const formData = form.data()
    let payload: Record<string, unknown> = {
      label: formData.label,
      field_type: formData.field_type,
      required: formData.required,
    }

    if (allowPermissions) {
      payload = {
        ...payload,
        visible: formData.visible,
        editable: formData.editable,
      }
    }

    if (formData.field_type === FieldTypes.rating) {
      payload = {
        ...payload,
        min_value: Number(ratingRange[0]),
        max_value: Number(ratingRange[1]),
      }
    } else if (
      formData.field_type === FieldTypes.singleChoice ||
      formData.field_type === FieldTypes.multipleChoice
    ) {
      payload = {
        ...payload,
        choice_options: formData.choice_options
          .filter((option: any) => option.label !== '')
          .map((option: any) => ({
            id: option.id,
            label: option.label,
            is_active: option.is_active,
          })),
      }
    }

    return payload
  }

  const { running, performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    try {
      app.setProgress({
        message: i18n.t('settings.custom_fields.saving'),
      })

      const payload = getPayload()

      if (onSubmit) {
        await onSubmit(payload)
      }

      if (trackingProperties)
        track(trackingEvents.factorialTip.tipClosedAutomatically, trackingProperties)

      closePortal()
    } finally {
      app.setProgress(null)
    }
  })

  const { data } = useQuery(DocumentsCustomFieldSettingsDocument)
  const canManage = data?.customFields._policies.companyCreateCustomFields.can || false

  const handleRatingRangeChange = ([min, max]: [number, number]) => {
    setRatingRange([Math.round(min), Math.round(max)])
  }

  const disabled = readonly || !canEditField(canManage, field) || running
  const isDisabledToEdit = (existingField && !duplicate) || disabled

  return (
    <Modal
      illustration={modalIllustration as any}
      title={modalTitle as any}
      description={modalDescription as any}
      onClose={onClose}
      onSubmit={performAction}
      footer={
        <CustomModalFooter
          readonly={readonly}
          field={field}
          form={form}
          modalButton={modalButton as any}
          disabled={disabled}
          canManage={canManage}
        />
      }
    >
      <FormRow>
        <TextField
          readonly={disabled}
          label={modalLabel}
          placeholder={modalPlaceholder}
          value={form.get('label').value}
          onChange={(value) => {
            form.get('label').set(value)
          }}
          mandatory
          extra={{ 'data-intercom-target': 'custom-field-modal-title' }}
        />
      </FormRow>
      {showRequired && (
        <FormRow>
          <LabeledCheckbox
            readonly={disabled}
            label={i18n.t('ats.custom_fields.label.required')}
            field={form.get('required')}
          />
        </FormRow>
      )}
      {showAllowedFieldTypes && options.length > 0 && (
        <FormRow>
          <Select
            none={i18n.t('settings.custom_fields.new.labels.choose')}
            label={i18n.t('settings.custom_fields.new.labels.field_type')}
            field={form.get('field_type')}
            options={options as any}
            readonly={isDisabledToEdit}
            showOriginalLabelText
            mandatory
          />
        </FormRow>
      )}
      <Choices form={form} disabled={readonly} />
      {form.get('field_type').value === FieldTypes.rating && (
        <FormRow>
          <RangeInput
            value={ratingRange as any}
            onChange={handleRatingRangeChange}
            disabled={isDisabledToEdit}
            range={range}
          />
        </FormRow>
      )}

      {form.get('field_type').value === FieldTypes.checkbox && (
        <Box paddingRight="s8" paddingBottom="s16">
          <Text.Normal size="200" color="grey800">
            {i18n.t('settings.custom_fields.new.labels.checkbox_description')}
          </Text.Normal>
        </Box>
      )}

      {allowPermissions && (
        <NoticeBox
          color="champagne400"
          message={
            field
              ? i18n.t('settings.custom_fields.new.permissions_edit')
              : i18n.t('settings.custom_fields.new.permissions_new')
          }
        />
      )}

      {modalAlert && <Alert color="warning">{modalAlert}</Alert>}
    </Modal>
  )
}

export default observer(FieldModal)
